import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Send, Megaphone, MessageSquare, Users, Plus, Search, X } from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';
import { supabase } from '../services/supabase';
import type { Database } from '../types/database.types';
import { useAuthStore } from '../store/authStore';
import {
  esCorreoEntranteDelClub,
  idsDelBuzonDelClub,
  listaIn,
  ROLES_DE_FAMILIA,
} from '../lib/buzonDelClub';
import { marcarLeidos, misLecturas } from '../lib/lecturas';
import { desgloseDeMotivos } from '../lib/motivoDeEntrega';
import { tutoresConCuenta } from '../services/guardians';
import { leerVarias } from '../lib/leerVarias';
// Cómo se llama y cómo se pinta una casa. Vivían aquí; se movieron a `lib`
// cuando la pantalla del entrenador pasó al mismo modelo de familia, para que
// una casa no pueda llamarse distinto según por qué puerta se mire.
import { nombreDeFamilia, inicialesDeFamilia } from '../lib/familias';

interface Message {
  id: string;
  sender_id: string;
  receiver_id: string | null;
  is_broadcast: boolean;
  content: string;
  read: boolean;
  created_at: string;
  sender?: { full_name: string };
}

type MessageRow = Database['public']['Tables']['messages']['Row'];
// Lo que devuelve la EF `anunciar-al-club`. No sale de `database.types.ts`
// —las Edge Functions no se generan— así que va escrito aquí, y el test
// `el-anuncio-dice-a-cuantos-llego.test.tsx` sirve el mismo objeto que la EF.
interface ParteDelAnuncio {
  avisos_escritos: number;
  /**
   * [346] Personas a las que va el aviso, antes de colapsar quien comparte móvil.
   *
   * ⚠️ OPCIONALES LOS DOS, Y NO POR DEJADEZ. El panel y la Edge Function se
   * despliegan por separado —Vercel con el merge, la EF cuando se ejecuta el
   * deploy—, así que existe un rato en el que esta pantalla es nueva y la EF
   * todavía es la vieja, que no manda estos campos. Si se leyeran como
   * obligatorios, el acuse reventaría encima de un anuncio que SÍ se ha
   * publicado: el operador vería un error y creería que no salió. Declarados
   * opcionales, ese rato se limita a no pintar una línea.
   */
  destinatarios?: number;
  /** Envíos que se hacen: uno por móvil, más uno por cada persona sin móvil. */
  moviles: number;
  /** [346] Quiénes comparten teléfono con otro, por sus ids. */
  destinatarios_colapsados?: string[];
  entregados: number;
  sin_movil: number;
  fallos: number;
  motivos: Record<string, number>;
  /** [345] La fila de INTENTO en `audit_log`, que es la identidad del anuncio. */
  anuncio_id: string;
  /** [345] Si el parte ha quedado GUARDADO. El `alert` es el acuse; la verdad es la fila. */
  parte_guardado: boolean;
}

/**
 * Una fila de `parte_de_los_anuncios()` (mig 345).
 *
 * ⚠️ NO se usa el tipo generado de `Database['public']['Functions']`. El
 * generador no expresa la nulabilidad de las columnas que devuelve una RPC:
 * saca `anuncio_id: string` y `moviles: number`, y los dos son NULL de verdad
 * para una tanda sin sellar. Sustituir esto por el generado haría que
 * TypeScript diera por imposible justo el caso que la pantalla tiene que
 * pintar —los 111 avisos anteriores a la 345—, y el `?? null` del render
 * pasaría por código muerto.
 *
 * Los LEÍDOS vienen CALCULADOS de los avisos, no guardados. Y `sellado` dice si
 * el anuncio salió de la RPC: los 111 avisos anteriores a la 345 no llevan
 * sello —el del 2 de octubre se escribió a mano durante la reparación de
 * #682—, así que se agrupan por su instante y NO tienen parte. Eso no es un
 * hueco que rellenar: es lo que pasó, y la pantalla lo dice.
 */
export interface AnuncioConParte {
  anuncio_id: string | null;
  tanda: string;
  titulo: string | null;
  escritos: number;
  leidos: number;
  descartados: number;
  sellado: boolean;
  intento_en: string | null;
  parte_en: string | null;
  destinatarios: number | null;
  colapsados: number | null;
  moviles: number | null;
  entregados: number | null;
  sin_movil: number | null;
  fallos: number | null;
  motivos: Record<string, number> | null;
}

// El parte, en palabras. «Un anuncio que se da por enviado y llega a un tercio es
// peor que uno que falla», así que aquí no cabe un «✅ enviado a todos»: se dicen
// las tres cifras y, cuando hay fallos de entrega, se dicen aparte y con su
// nombre. Un «no tiene la app» no se arregla desplegando nada; un 403 sí, y
// juntarlos es como se esconde un fallo detrás de una cifra tranquilizadora.
export function parteEnPalabras(p: ParteDelAnuncio): string {
  const personas = p.avisos_escritos === 1 ? '1 persona' : `${p.avisos_escritos} personas`;
  const lineas = [
    `✅ Anuncio publicado para ${personas}: lo tienen en su bandeja de avisos.`,
    '',
    `Ha sonado en ${p.entregados} de ${p.moviles} móviles.`,
  ];
  // [346] POR QUÉ LOS MÓVILES SON MENOS QUE LAS PERSONAS. Sin esta línea, el
  // acuse diría «90 móviles» para 91 destinatarios y el número no se podría
  // cuadrar — que es justo el defecto que esta pantalla viene arreglando.
  const nColapsados = p.destinatarios_colapsados?.length ?? 0;
  if (nColapsados > 0) {
    const n = nColapsados;
    lineas.push(
      `(${p.destinatarios} personas y ${p.moviles} móviles: ${n} comparte${n === 1 ? '' : 'n'} ` +
        'teléfono con otra, y ese teléfono suena una vez.)'
    );
  }
  if (p.sin_movil > 0) {
    lineas.push(
      `⚠️ A ${p.sin_movil} no les ha sonado el teléfono (sin la app instalada o con los avisos apagados): lo verán al abrir la app.`
    );
  }
  if (p.fallos > 0) {
    const desglose = desgloseDeMotivos(p.motivos);
    lineas.push(
      `❌ ${p.fallos} fallo(s) de entrega. Esto NO es «no tiene la app»: el envío se rompió. Dilo para que se mire (${desglose}).`
    );
  }
  if (p.moviles === 0) {
    lineas.push('⚠️ No ha sonado NINGÚN teléfono. El anuncio está en las bandejas, pero nadie lo sabrá hasta abrir la app.');
  }
  // [345] Y si el parte no ha quedado guardado hay que decirlo AQUÍ, porque
  // este acuse se cierra y no vuelve. Callarlo afirmaría un registro que no
  // existe, que es la forma exacta del «✅ Mensaje enviado a todos» del que
  // venimos: el anuncio sí salió, pero abajo no aparecerá.
  if (!p.parte_guardado) {
    lineas.push('');
    lineas.push(
      '⚠️ El anuncio SÍ se ha publicado, pero su parte no ha quedado registrado: no lo verás en la lista de abajo. Apunta estas cifras.'
    );
  }
  return lineas.join('\n');
}

// `functions.invoke` no pone el cuerpo del error en `error.message`: en un no-2xx
// deja la respuesta cruda en `error.context`. Sin leerla, un «solo dirección
// puede anunciar al club» se le enseña a Jose como «Edge Function returned a
// non-2xx status code», que no dice nada.
async function motivoDelFallo(err: unknown): Promise<string> {
  const contexto = (err as { context?: unknown } | null | undefined)?.context;
  if (contexto instanceof Response) {
    try {
      const cuerpo = (await contexto.json()) as { error?: unknown };
      if (typeof cuerpo?.error === 'string' && cuerpo.error) return cuerpo.error;
    } catch {
      // Cuerpo no-JSON (una página de error del gateway): nos queda el message.
    }
  }
  if (err && typeof err === 'object' && 'message' in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === 'string' && m) return m;
  }
  return 'error desconocido';
}
// Embed `profiles!sender_id(full_name)` — supabase-js devuelve la relación
// bajo la key `profiles` (la disambiguación por FK no se refleja en el key
// del resultado, solo en el query string).
type MessageRowWithSender = MessageRow & {
  profiles: { full_name: string } | null;
};
interface Alumno {
  id: string;
  full_name: string;
  role?: string | null;
}

/**
 * Un hilo del buzón. Desde la mig 277 la unidad NO es el alumno: es la familia.
 *
 * El club no habla con un niño, habla con una casa. Con dos hermanos apuntados
 * había dos conversaciones separadas que decían lo mismo, y quien contestaba en
 * una no veía lo respondido en la otra.
 *
 * `key` la calcula `family_key()` en la base y NO se deduce aquí: tiene que
 * salir idéntica en el panel y en la app, o el club vería dos hilos donde la
 * familia ve uno.
 */
interface Familia {
  key: string;
  nombre: string;
  miembros: Alumno[];
}

interface Conversation {
  familia: Familia;
  lastMessage: Message | null;
  /**
   * Cuántos NO HE LEÍDO YO, o `null` si no se ha podido saber (mig 324).
   *
   * `null` y no 0: un 0 apaga el globo y le dice a un director «lo tienes todo
   * visto», que es el fallo que esta serie viene a quitar.
   */
  unreadCount: number | null;
}

// Con familia elegida, `destinatarioId` solo se queda a null si la familia
// llegó SIN MIEMBROS: el efecto que lo calcula cae a `miembros[0]?.id`.
//
// Hoy eso no se puede dar —`familias` agrupa a partir de `alumnos`, así que
// toda familia nace con al menos uno—, y se deja dicho a propósito: es la
// tercera condición del `disabled` del botón de enviar, y un botón apagado por
// un motivo que no se enseña es un callejón sin salida. Si algún día una
// familia llega vacía, el panel lo dirá en vez de quedarse mudo.
const MOTIVO_SIN_DESTINATARIO =
  'No hay a quién escribir: esta familia llegó sin miembros. Recargue la lista de conversaciones.';

function msgTime(dateStr: string) {
  const d = new Date(dateStr);
  if (isToday(d)) return format(d, 'HH:mm');
  if (isYesterday(d)) return 'Ayer';
  return format(d, 'dd/MM');
}

// El nombre que firma una burbuja del hilo.
//
// Solo firma el lado del club: enfrente hay una sola persona y ya la nombra la
// cabecera de la conversación, así que repetirla en cada burbuja es ruido. En
// el lado del club, en cambio, desde #483 pueden escribir el admin y cualquier
// director, y sin firma los mensajes son indistinguibles entre sí.
//
// Sin fallback, a propósito. Si el nombre no viene, la burbuja se queda sin
// firma. Es la lección del #469: un "Alumno" inventado se lee como un dato y
// nadie lo cuestiona; un hueco se lee como lo que es.
function autorDelClub(m: Message, delBuzon: boolean, miId: string | undefined): string {
  if (!delBuzon) return '';
  if (m.sender_id === miId) return 'Tú';
  return m.sender?.full_name?.trim() ?? '';
}

/**
 * La marca de "de cuál de los hermanos va este mensaje".
 *
 * Solo aparece cuando el hilo junta a más de uno, que es cuando hace falta: con
 * un solo miembro ya lo dice la cabecera y repetirlo en cada burbuja es el
 * mismo ruido que evita `autorDelClub`.
 *
 * Mira el lado: si el mensaje es del club, la persona que importa es a QUIÉN se
 * le mandó; si es de la familia, QUIÉN lo escribió.
 *
 * Sin fallback, igual que `autorDelClub` y por la misma razón (#469): si el
 * nombre no está, no hay marca. Un destinatario inventado en un hilo de dos
 * hermanos no es un detalle cosmético — se lee como un dato y decide a quién
 * crees que estás contestando.
 */
function miembroDelMensaje(m: Message, delBuzon: boolean, miembros: Alumno[]): string {
  if (miembros.length < 2) return '';
  const quienId = delBuzon ? m.receiver_id : m.sender_id;
  if (!quienId) return '';
  return miembros.find((x) => x.id === quienId)?.full_name?.trim() ?? '';
}

/**
 * Quién pasa por `family_key()`: **solo los alumnos**. Los otros dos roles del
 * buzón quedan fuera, cada uno por su motivo.
 *
 * **El MONITOR, porque no es familia.** También aparece en esta lista —el club
 * puede escribirle— pero esos mensajes son una relación entrenador–jugador.
 * Agruparlos metería a un hermano en una conversación deportiva que no es suya.
 * Medido: hay un monitor con 3 mensajes en el buzón. Y se le excluye por ROL y
 * no confiando en que su clave salga suelta, porque si algún día un monitor
 * joven tuviera `parent_email`, el escalón 2 de `family_key()` lo metería en la
 * familia de un alumno sin que nadie lo notara hasta leer el hilo.
 *
 * **El TUTOR, porque su clave propia sería un singleton** — ver
 * `heredaLaClaveDeSusHijos`, justo debajo. Sí es familia; lo que no es, es
 * alumno.
 */
function pideClaveFamiliar(a: Alumno): boolean {
  return a.role === 'alumno';
}

/**
 * Y el TUTOR tampoco pasa por `family_key()`, aunque sí sea familia.
 *
 * `family_key` busca en `guardian_students` por `student_id`, y un tutor no es
 * alumno: cae al escalón 3 y saca una clave propia. Medido en producción:
 *
 *     family_key(Adidas) = family_key(Nike) = 2d940fc5…
 *     family_key(Nike Junior, su tutor)     = 1d9cab9c…
 *
 * Un singleton — el club vería «Nike Junior» en una conversación y «Adidas y
 * Nike» en otra, sin nada que dijera que son la misma casa. Así que el tutor
 * **hereda** la clave de sus hijos, resuelta por donde está escrito el vínculo
 * (`guardians` + `guardian_students`, ver `tutoresConCuenta`).
 */
function heredaLaClaveDeSusHijos(a: Alumno): boolean {
  return a.role === 'tutor';
}

export default function ComunicacionesPage() {
  const profile = useAuthStore((s) => s.profile);
  const [tab, setTab] = useState<'bandeja' | 'broadcast'>('bandeja');
  const [messages, setMessages] = useState<Message[]>([]);
  // QUÉ HE LEÍDO YO, por id de mensaje (mig 324). `null` = no se ha podido
  // saber, que no es lo mismo que «no he leído nada»: con un Set vacío la
  // bandeja pintaría TODO como no leído, y con un 0 diría que está todo visto.
  const [misLeidos, setMisLeidos] = useState<Set<string> | null>(new Set());
  const [alumnos, setAlumnos] = useState<Alumno[]>([]);
  // Los ids del buzón del club (el admin) más el propio. Ver `lib/buzonDelClub`.
  const [buzonIds, setBuzonIds] = useState<string[]>([]);
  // «Sin conversaciones» es una afirmación sobre el buzón del club. Sin haber
  // podido leer QUIÉNES lo forman, la lista se construye con el propio id y
  // sale vacía por el motivo equivocado — el fallo que esta página vino a
  // arreglar, volviendo con la misma cara.
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  // id de persona -> clave de su familia, tal y como la da la base (mig 277).
  const [clavesFamiliares, setClavesFamiliares] = useState<Map<string, string>>(new Map());
  const [avisoAgrupacion, setAvisoAgrupacion] = useState<string | null>(null);
  const [selectedFamilia, setSelectedFamilia] = useState<Familia | null>(null);
  // A cuál de la familia se le contesta. `messages` sigue siendo persona a
  // persona (la 277 no toca ni una fila), así que una respuesta va a alguien
  // concreto aunque el hilo sea de la casa.
  const [destinatarioId, setDestinatarioId] = useState<string | null>(null);
  const [threadMessages, setThreadMessages] = useState<Message[]>([]);
  // «Sé el primero en enviar un mensaje» es una afirmación sobre un hilo que
  // no se ha podido leer. Con el error descartado, el admin abría una
  // conversación con historia y la veía en blanco: o escribe encima de lo que
  // ya se dijo, o concluye que la familia nunca escribió.
  const [errorHilo, setErrorHilo] = useState<string | null>(null);
  // Por qué no se ha enviado el último mensaje. El `error` del insert se
  // descartaba: el admin pulsaba enviar, el mensaje no aparecía y no se decía
  // nada. Una escritura rechazada era indistinguible de una enviada.
  const [errorEnvio, setErrorEnvio] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [broadcastText, setBroadcastText] = useState('');
  const [broadcastTitle, setBroadcastTitle] = useState('');
  // [345] El histórico de anuncios con su parte. `null` = todavía no se sabe,
  // que no es lo mismo que «no hay ninguno»: una lista vacía por un error de
  // lectura diría «el club nunca ha anunciado nada», y eso es una afirmación
  // falsa sobre lo que sí pasó.
  const [anuncios, setAnuncios] = useState<AnuncioConParte[] | null>(null);
  const [errorAnuncios, setErrorAnuncios] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const selectedFamiliaRef = useRef<Familia | null>(null);
  // Si dirección eligió hermano a mano, no se le mueve debajo mientras escribe.
  const elegidoAManoRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    selectedFamiliaRef.current = selectedFamilia;
  }, [selectedFamilia]);

  useEffect(() => {
    if (!profile?.id) return;
    loadData();
    const channel = supabase
      .channel('messages-admin')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, () => {
        loadData();
        if (selectedFamiliaRef.current) loadThread(selectedFamiliaRef.current);
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // Dep deliberada `[profile?.id]`: solo el id del usuario determina la
    // suscripción. El handler accede a `selectedUserRef.current` (no `selectedUser`
    // como dep) para leer el chat activo más reciente sin re-suscribir el canal
    // cada vez que el admin cambia de conversación.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  useEffect(() => {
    if (selectedFamilia) loadThread(selectedFamilia);
    // Dep deliberada `[selectedFamilia]`: el efecto carga el hilo al cambiar de
    // conversación. `loadThread` se redefine en cada render (no memoizado);
    // listarlo aquí dispararía la carga en cada render y, peor, mientras el
    // usuario está escribiendo. Lectura por closure es la semántica correcta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFamilia]);

  // A quién contesta el club: al último de la casa que escribió. Con un solo
  // miembro es él; con dos hermanos es el que preguntó, que es lo que uno
  // espera al pulsar responder.
  //
  // ⚠️ Hace falta el ref y no vale con "si ya hay uno, respétalo". El efecto
  // corre DOS veces: primero al elegir la familia, cuando el hilo todavía está
  // vacío y el único candidato es el primer miembro por orden alfabético, y
  // otra vez al llegar los mensajes. Respetando el valor previo se quedaba
  // clavado el primero de la lista —Adidas donde tenía que poner Nike—, que es
  // el hermano equivocado y sin nada que lo delate.
  useEffect(() => {
    if (!selectedFamilia) {
      setDestinatarioId(null);
      elegidoAManoRef.current = false;
      return;
    }
    if (elegidoAManoRef.current) return;
    const ids = new Set(selectedFamilia.miembros.map((m) => m.id));
    const ultimo = [...threadMessages].reverse().find((m) => ids.has(m.sender_id));
    setDestinatarioId(ultimo?.sender_id ?? selectedFamilia.miembros[0]?.id ?? null);
  }, [selectedFamilia, threadMessages]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [threadMessages]);
  useEffect(() => {
    if (showSearch) inputRef.current?.focus();
  }, [showSearch]);

  async function loadData() {
    if (!profile?.id) return;
    // El buzón del club, no "lo mío". Un director entraba aquí y pedía SUS
    // conversaciones, que están vacías: los mensajes al club van dirigidos al
    // admin. Ver `lib/buzonDelClub.ts`.
    const { ids: buzon, error: errorBuzon } = await idsDelBuzonDelClub(profile.id);
    // Sin el buzón, la lista se construiría con `[miId]`: un director vería SUS
    // conversaciones, que están vacías. Es exactamente el fallo que esta página
    // vino a arreglar, y volvería con la misma cara de «no hay nada».
    if (errorBuzon) {
      setErrorCarga(`No se ha podido cargar el buzón del club: ${errorBuzon}`);
      return;
    }
    setErrorCarga(null);
    setBuzonIds(buzon);
    const enElBuzon = listaIn(buzon);
    // Las dos por `leerVarias`: el `error` no está en una posición que se
    // pueda omitir, y cada consulta trae su nombre para poder contar CUÁL
    // falló. Con el destructuring de antes —`[{ data: msgs }, { data: als }]`—
    // los dos errores se caían solos, y es la forma exacta del #496.
    const {
      datos,
      fallidos,
      error: primerError,
    } = await leerVarias([
      {
        que: 'las conversaciones',
        consulta: supabase
          .from('messages')
          .select('*, profiles!sender_id(full_name)')
          .or(`receiver_id.in.${enElBuzon},sender_id.in.${enElBuzon}`)
          .eq('is_broadcast', false)
          .order('created_at', { ascending: false })
          .limit(200),
      },
      {
        que: 'la lista de personas',
        consulta: supabase
          .from('profiles')
          // `role` no es decorativo: decide quién pasa por `family_key()`, quién
          // hereda la clave de sus hijos y quién va suelto. Ver los tres
          // predicados de arriba.
          .select('id, full_name, role')
          // Alumnos, monitores Y TUTORES. Los tutores entran aquí desde el
          // arreglo del buzón ciego: un tutor es `role = 'tutor'`, así que con la
          // lista anterior no estaba en ninguna familia y **su mensaje al club no
          // aparecía en ninguna conversación**. No se notaba porque hasta la fase
          // C de la 277 el tutor ni siquiera veía el hilo para contestar.
          //
          // Esta lista alimenta también los contactos del chat 1-a-1 y el fan-out
          // del push del broadcast; ese último sigue SIN tutores a propósito
          // (ver `sendBroadcast`). RLS ya permite la lectura; se excluyen
          // admin/director a propósito.
          // [badge] La lista sube a `lib/buzonDelClub` porque de ella sale
          // también lo que CUENTA el círculo de la barra lateral. Eran dos
          // listas iguales en dos sitios; el día que una creciera, el número y
          // la lista dejarían de decir lo mismo sin ponerse nada en rojo.
          .in('role', ROLES_DE_FAMILIA as unknown as string[])
          .eq('active', true)
          .order('full_name'),
      },
    ]);
    const [msgs, als] = datos;
    // Sin los mensajes no hay bandeja: la lista saldría vacía y la pantalla lo
    // remataría con «Sin conversaciones», que es una afirmación sobre el buzón
    // del club. Se corta aquí, igual que con el buzón ilegible.
    if (fallidos.includes('las conversaciones')) {
      setErrorCarga(
        `No se han podido cargar las conversaciones: ${primerError ?? 'error desconocido'}`
      );
      return;
    }
    const msgRows = (msgs ?? []) as unknown as MessageRowWithSender[];
    setMessages(msgRows.map((m) => ({ ...m, sender: m.profiles ?? undefined })));
    // La lista de personas SÍ es best-effort: sin ella los hilos siguen
    // estando —salen de los mensajes—, pero sin nombres ni agrupación por
    // familia. Se dice y se sigue, que es mejor que una bandeja vacía.
    const listaIlegible = fallidos.includes('la lista de personas');
    const lista = (als ?? []) as Alumno[];
    // Las claves ANTES de publicar la lista, y las dos juntas.
    //
    // Si se publicaran los alumnos y las claves llegaran después, la bandeja
    // pintaría un instante con la agrupación VIEJA —cada hermano por su
    // lado— y se remontaría al llegar la respuesta. Dos daños: enseña una
    // agrupación que no es, y quien pulse en esa rendija pulsa sobre una fila
    // que React ya ha sustituido, así que no pasa nada. Costó un test en verde
    // y en rojo alternos hasta verlo.
    const { claves, aviso } = await pedirClavesFamiliares(lista);
    const avisoTutores = await engancharTutores(lista, claves);
    setAlumnos(lista);
    setClavesFamiliares(claves);
    // Dos avisos posibles y un solo hueco: se enseñan los que haya. Callar el
    // segundo porque ya hay un primero es la misma enfermedad que arregla el
    // aviso en sí.
    setAvisoAgrupacion(
      [
        listaIlegible
          ? 'No se ha podido leer la lista de personas: los hilos se ven, pero sin nombres ni agrupación por familia.'
          : null,
        aviso,
        avisoTutores,
      ]
        .filter(Boolean)
        .join(' ') || null
    );

    // Y QUIÉN los ha leído, que desde la mig 324 no es `messages.read`: el buzón
    // lo comparten siete personas y ese booleano era del mensaje, así que el
    // primero que abría un hilo lo apagaba para los otros seis. Medido en
    // producción: 21 de 21 mensajes del buzón marcados como leídos.
    //
    // ⚠️ La bandeja ENSEÑARÁ no leídos que antes no enseñaba. No es una
    // regresión: antes decía que estaban leídos sin que lo estuvieran.
    //
    // ⚠️ Y VA AL FINAL, DESPUÉS DE PUBLICAR LA LISTA. Estaba en medio del
    // cargador, y eso ataba la bandeja a esta lectura: si `message_reads` no se
    // podía leer —o simplemente tardaba—, las conversaciones no llegaban a
    // pintarse y la pantalla remataba con «Sin conversaciones. Usa + para
    // iniciar una», que es una afirmación sobre el buzón del club. Lo cazó el CI.
    // Los globos son un adorno encima de la lista; la lista no espera por ellos.
    const { leidos, error: errorLeidos } = await misLecturas(
      msgRows.map((m) => m.id),
      profile.id
    );
    setMisLeidos(errorLeidos ? null : leidos);
  }

  /**
   * Mete a cada tutor en la familia de sus hijos, **mutando el mapa de claves**.
   *
   * Su clave propia no vale: `family_key` la saca del escalón 3 para cualquiera
   * que no sea alumno, y un singleton es justo lo que hay que evitar —el club
   * vería «Nike Junior» en una conversación y «Adidas y Nike» en otra—. El
   * vínculo se lee de donde está escrito (`guardians` + `guardian_students`).
   *
   * Solo se pregunta si hay algún tutor en la lista: sin ninguno no hay nada
   * que enganchar y sobra el viaje.
   */
  async function engancharTutores(
    lista: Alumno[],
    claves: Map<string, string>
  ): Promise<string | null> {
    const tutores = lista.filter(heredaLaClaveDeSusHijos);
    if (tutores.length === 0) return null;

    let vinculos;
    try {
      vinculos = await tutoresConCuenta();
    } catch (e) {
      // Sin los vínculos, un tutor cae a su propia conversación. Se dice: una
      // fila suelta con el nombre del padre al lado del hilo de sus hijos se
      // lee como una familia más, no como un fallo.
      const motivo = e instanceof Error ? e.message : 'error desconocido';
      return `No se pudieron enganchar los tutores a su familia (${motivo}). Puede que alguno aparezca en una conversación aparte.`;
    }

    const hijosPorTutor = new Map(vinculos.map((t) => [t.profile_id, t.student_ids]));
    for (const t of tutores) {
      // La clave del PRIMER hijo que tenga una. Da igual cuál: si son hermanos
      // comparten clave, y si no la comparten es que no son la misma familia,
      // en cuyo caso el tutor tiene que caer en una de las dos y ninguna es más
      // correcta que la otra.
      const clave = (hijosPorTutor.get(t.id) ?? [])
        .map((id) => claves.get(id))
        .find((k): k is string => !!k);
      if (clave) claves.set(t.id, clave);
    }
    // Un tutor sin hijos vivos —revocados, o que nunca tuvo— se queda sin
    // clave y cae a su propia conversación. NO se avisa: es un estado real y
    // esperado, hay uno así en producción, y su hilo a su nombre es la lectura
    // correcta. El aviso se reserva para cuando NO SE HA PODIDO mirar.
    return null;
  }

  /**
   * Las claves de familia, de una tacada.
   *
   * `family_keys()` (mig 277) resuelve la lista entera en un viaje. Se PREGUNTA
   * a la base a propósito: replicar aquí los tres escalones —tutor vinculado,
   * `parent_email`, id propio— sería tener dos implementaciones de la misma
   * regla, y el día que discreparan el club vería dos hilos donde la familia ve
   * uno, sin dar ningún error.
   */
  async function pedirClavesFamiliares(
    lista: Alumno[]
  ): Promise<{ claves: Map<string, string>; aviso: string | null }> {
    const ids = lista.filter(pideClaveFamiliar).map((a) => a.id);
    if (ids.length === 0) return { claves: new Map(), aviso: null };

    const { data, error } = await supabase.rpc('family_keys', { p_people: ids });
    if (error) {
      // Sin claves la pantalla sigue funcionando —un hilo por persona, como
      // antes de la 277— pero LO DICE. Desagruparse en silencio es peor que
      // fallar: un hilo de dos hermanos partido en dos se lee exactamente igual
      // que una familia que solo tiene un hijo apuntado, y nadie lo cuestiona.
      return {
        claves: new Map(),
        aviso: `No se pudieron agrupar las familias (${error.message}). Se muestra una conversación por persona.`,
      };
    }

    const claves = new Map<string, string>();
    let sinClave = 0;
    for (const fila of data ?? []) {
      if (fila.family_key) claves.set(fila.id, fila.family_key);
      else sinClave += 1;
    }
    // `family_keys` devuelve NULL —no omite la fila— para quien el que llama no
    // puede ver. Un admin lo ve todo, así que esto no debería pasar nunca; si
    // pasa, se dice en vez de dejar un hueco que parece un dato.
    return {
      claves,
      aviso:
        sinClave > 0
          ? `${sinClave} persona(s) sin clave familiar: se muestran en su propia conversación.`
          : null,
    };
  }

  async function loadThread(fam: Familia) {
    // El motivo del último envío fallido muere con la conversación: pegado en
    // el hilo de otra casa diría algo falso de ella.
    setErrorEnvio(null);
    // La conversación es entre esa FAMILIA y EL BUZÓN. Dos generalizaciones en
    // la misma consulta, y ninguna es cosmética:
    //   · el buzón, no yo (#483): si no, un director abre un hilo del club y lo
    //     ve vacío;
    //   · la familia, no una persona (mig 277): si no, dos hermanos son dos
    //     conversaciones que dicen lo mismo.
    const club = buzonIds.length > 0 ? buzonIds : [profile?.id ?? ''];
    const idsFamilia = fam.miembros.map((m) => m.id);
    const enElBuzon = listaIn(club);
    const enLaFamilia = listaIn(idsFamilia);
    const { data, error: errorLectura } = await supabase
      .from('messages')
      .select('*, profiles!sender_id(full_name)')
      .or(
        `and(sender_id.in.${enElBuzon},receiver_id.in.${enLaFamilia}),and(sender_id.in.${enLaFamilia},receiver_id.in.${enElBuzon})`
      )
      .order('created_at');
    if (errorLectura) {
      // Se vacía a propósito: `threadMessages` puede llevar los de la familia
      // ANTERIOR (nadie los limpia al cambiar de conversación), y dejarlos
      // puestos pondría los mensajes de una casa bajo el nombre de otra. El
      // aviso ocupa el hueco, así que no queda un blanco que decir nada.
      setThreadMessages([]);
      setErrorHilo(errorLectura.message || 'Error desconocido');
      return;
    }
    setErrorHilo(null);
    const rows = (data ?? []) as unknown as MessageRowWithSender[];
    setThreadMessages(rows.map((m) => ({ ...m, sender: m.profiles ?? undefined })));
    // Registrar que LOS HE LEÍDO YO (mig 324), no marcarlos leídos para todos.
    //
    // Antes esto ponía `read = true` en los mensajes que recibió el buzón —un
    // director SÍ puede: la policy "Mensajes: admin puede todo" es ALL con
    // `is_admin()`, que le incluye—, y con eso apagaba el no leído de los otros
    // seis. Ahora deja UNA FILA CON MI NOMBRE y los demás lo siguen teniendo sin
    // abrir, que es la verdad.
    if (!profile) return;
    // [badge] EL MISMO PREDICADO, TERCERA VEZ Y ÚLTIMA. Aquí estaba escrito a
    // mano («sender en la familia y receiver en el club»), que es lo que la
    // función dice; pero si alguna vez dejaran de coincidir, habría mensajes que
    // se cuentan y se pintan y que NINGUNA apertura puede marcar — que es
    // exactamente el 4 atascado del que viene este PR, solo que por el otro
    // extremo.
    const delHilo = rows
      .filter((m) => esCorreoEntranteDelClub(m, new Set(club), new Set(idsFamilia)))
      .map((m) => m.id);
    const { error } = await marcarLeidos(delHilo, profile.id);
    // Marcar como leído no tiene nada que el admin pueda arreglar, así que no
    // se le pone un aviso delante. Lo que NO se hace es apagar el indicador de
    // no leído si la base no lo ha guardado: volvería a encenderse en la
    // siguiente recarga sin explicación.
    if (error) return;
    // El estado local que se mueve es EL MÍO: antes se marcaba `read: true` en
    // la fila del mensaje, que es justo lo que ya no significa «lo he leído yo».
    setMisLeidos((prev) => (prev === null ? prev : new Set([...prev, ...delHilo])));
  }

  async function sendReply() {
    // Lo que el admin ya ve en pantalla se calla: la caja vacía o ninguna
    // familia elegida no necesitan explicación.
    if (!reply.trim() || !selectedFamilia) return;
    // Lo que NO ve, se dice. Sin destinatario ni sesión el botón salía
    // deshabilitado o el `return` cortaba sin abrir la boca.
    if (!profile) {
      setErrorEnvio('No hay sesión cargada. Recargue la página antes de escribir.');
      return;
    }
    if (!destinatarioId) {
      setErrorEnvio(MOTIVO_SIN_DESTINATARIO);
      return;
    }
    setSending(true);
    setErrorEnvio(null);
    const content = reply.trim();
    const { data, error } = await supabase
      .from('messages')
      .insert({ sender_id: profile.id, receiver_id: destinatarioId, content })
      .select('id, created_at, sender_id, receiver_id, content, read, is_broadcast')
      .single();
    // El motivo delante y el texto intacto en la caja. Hasta aquí un rechazo
    // —RLS, sesión caducada, red— salía por este `return` sin dejar rastro en
    // ningún sitio: ni fila, ni aviso, ni consola. El mismo patrón que arregló
    // §8 en el chat del monitor, que ya restaura el texto y enseña el motivo.
    if (error) {
      setErrorEnvio(`No se pudo enviar el mensaje: ${error.message}`);
      setSending(false);
      return;
    }
    if (!data) {
      setErrorEnvio(
        'No se pudo confirmar el envío: la base no devolvió el mensaje. No lo dé por enviado.'
      );
      setSending(false);
      return;
    }
    setThreadMessages((prev) => {
      if (prev.some((m) => m.id === data.id)) return prev;
      const inserted: Message = {
        id: data.id,
        sender_id: data.sender_id,
        receiver_id: data.receiver_id,
        is_broadcast: data.is_broadcast,
        content: data.content,
        read: data.read,
        created_at: data.created_at,
        sender: { full_name: profile.full_name ?? '' },
      };
      return [...prev, inserted];
    });
    setReply('');
    supabase.functions
      .invoke('gym-send-push', {
        body: {
          user_id: destinatarioId,
          title: profile.full_name ?? 'Admin',
          body: content.slice(0, 100),
          type: 'chat',
          data: { screen: 'Chat' },
        },
      })
      .catch(() => {});
    setSending(false);
  }

  /**
   * El histórico de anuncios con su parte. [mig 345]
   *
   * El error NO se descarta: hasta ahora el parte se pintaba en un `alert()` y
   * se tiraba, así que esta lista es el único sitio donde queda. Una lista
   * vacía por un error de lectura se leería como «no hay anuncios», que es la
   * misma enfermedad del BUG 13 —el chat que se veía vacío— y la de la propia
   * 330, donde el panel decía «✅ Mensaje enviado a todos» pasara lo que
   * pasara.
   */
  const cargarAnuncios = useCallback(async () => {
    const { data, error } = await supabase.rpc('parte_de_los_anuncios');
    if (error) {
      setErrorAnuncios(error.message || 'Error desconocido');
      // Y NO se pone `[]`: `null` sigue significando «no se sabe», y el aviso
      // ocupa el hueco en vez de una tabla vacía que afirma algo.
      return;
    }
    setErrorAnuncios(null);
    setAnuncios((data ?? []) as unknown as AnuncioConParte[]);
  }, []);

  useEffect(() => {
    // Solo al abrir la pestaña: es una lectura que agrega sobre `notifications`
    // y no hace falta tenerla en marcha mientras se trabaja en la bandeja.
    if (tab === 'broadcast') void cargarAnuncios();
  }, [tab, cargarAnuncios]);

  async function sendBroadcast() {
    if (!broadcastTitle.trim() || !broadcastText.trim() || !profile) return;
    setSending(true);
    const title = broadcastTitle.trim();
    const body = broadcastText.trim();
    const { error: msgError } = await supabase
      .from('messages')
      .insert({ sender_id: profile.id, is_broadcast: true, content: body });
    // Se para ANTES del reparto de push: mandar la notificación de un anuncio
    // que no ha quedado registrado es peor que no mandarla. El admin veía
    // "✅ Mensaje enviado a todos" pasara lo que pasara.
    if (msgError) {
      alert(`No se pudo enviar el anuncio: ${msgError.message}`);
      setSending(false);
      return;
    }
    // UNA SOLA LLAMADA, Y DEVUELVE EL PARTE. [mig 330/331 + EF anunciar-al-club]
    //
    // De dónde viene esto: el panel escribía UNA fila de aviso con
    // `user_id: null` —que el primero en descartarla descartaba para los 109— y
    // después recorría SU PROPIA lista para los push. La mig 330 arregló la
    // fila; la 331 y esta EF arreglan el móvil.
    //
    // LA PANTALLA YA NO DECIDE NADA DE ESTO, y es el punto: ni a quién se le
    // escribe (lo dice `anunciar_al_club`, en SQL), ni en qué móvil suena (lo
    // dice `push_recipients` a través de `a_quien_le_suena` — para un menor que
    // aún no puede entrar suena en el del tutor), ni si puede sonar (lo dice
    // `gym-send-push`, con sus cuatro reglas). Cada criterio que esta pantalla
    // se inventara sería un segundo sitio decidiendo lo mismo: es lo que costó
    // la serie de /pedidos, y es lo que me costó la propia 330, donde el
    // `con_push` que calculé del token propio contaba 39 donde había 34 y hacía
    // que el panel se saltara entregas reales.
    const { data: parte, error: efError } = await supabase.functions.invoke<ParteDelAnuncio>(
      'anunciar-al-club',
      { body: { title, body } }
    );

    if (efError || !parte) {
      // `functions.invoke` no pone el cuerpo del error en `error.message`: en un
      // no-2xx deja la respuesta cruda en `error.context`. Sin esto, un «solo
      // dirección puede anunciar al club» se vería como «Edge Function returned
      // a non-2xx status code», que no dice nada a quien está delante.
      const motivo = await motivoDelFallo(efError);
      alert(`No se ha podido publicar el anuncio: ${motivo}`);
      setSending(false);
      return;
    }

    setBroadcastTitle('');
    setBroadcastText('');
    alert(parteEnPalabras(parte));
    // [345] El `alert` es el acuse inmediato; la verdad es la fila. Se recarga
    // para que el anuncio que se acaba de mandar aparezca abajo con su parte, y
    // para que —si `parte_guardado` fue false— se vea que NO está.
    await cargarAnuncios();
    setSending(false);
  }

  // Los ids que cuentan como "el club" al pintar, con el mismo repliegue que
  // `loadThread`: si el buzón todavía no ha cargado, al menos el propio id.
  const idsDelHilo = useMemo(
    () => (buzonIds.length > 0 ? buzonIds : profile?.id ? [profile.id] : []),
    [buzonIds, profile?.id]
  );

  // Un botón apagado sin motivo es un callejón sin salida. Se ordena de lo que
  // no se ve a lo que sí: primero lo que el admin no puede adivinar, y la caja
  // vacía —lo único evidente— al final.
  const motivoEnvioDeshabilitado = sending
    ? 'Enviando…'
    : !profile
      ? 'No hay sesión cargada. Recargue la página.'
      : !destinatarioId
        ? MOTIVO_SIN_DESTINATARIO
        : !reply.trim()
          ? 'Escriba un mensaje.'
          : null;

  const familias = useMemo<Familia[]>(() => {
    const porClave = new Map<string, Alumno[]>();
    for (const a of alumnos) {
      // Sin clave de familia el hilo es suyo y de nadie más: un monitor, o
      // alguien que la RPC no pudo resolver. El prefijo `solo:` evita que ese
      // apaño pueda chocar con una clave real, que es un uuid.
      // El mapa manda: lleva la clave de los alumnos (`family_key`) y la que el
      // tutor hereda de sus hijos. Quien no está en él —un monitor, o alguien
      // que no se pudo resolver— va a su propia conversación. El prefijo
      // `solo:` evita que ese apaño choque con una clave real, que es un uuid.
      const clave = clavesFamiliares.get(a.id) ?? `solo:${a.id}`;
      const grupo = porClave.get(clave);
      if (grupo) grupo.push(a);
      else porClave.set(clave, [a]);
    }
    return Array.from(porClave.entries()).map(([key, miembros]) => {
      const ordenados = [...miembros].sort((x, y) => x.full_name.localeCompare(y.full_name, 'es'));
      return { key, miembros: ordenados, nombre: nombreDeFamilia(ordenados) };
    });
  }, [alumnos, clavesFamiliares]);

  // El buzón como conjunto: el predicado compartido pregunta por pertenencia, y
  // `buzonIds` es un array que se re-crea en cada carga.
  const enElBuzonSet = useMemo(() => new Set(buzonIds), [buzonIds]);

  const conversaciones = useMemo<Conversation[]>(() => {
    const out: Conversation[] = [];
    for (const f of familias) {
      const ids = new Set(f.miembros.map((m) => m.id));
      const msgs = messages.filter(
        (m) => ids.has(m.sender_id) || (m.receiver_id !== null && ids.has(m.receiver_id))
      );
      if (msgs.length === 0) continue;
      const sorted = [...msgs].sort(
        (x, y) => new Date(y.created_at).getTime() - new Date(x.created_at).getTime()
      );
      // Los no leídos son los de TODA la casa: si escribe un hermano y el otro
      // no, el hilo sigue teniendo algo sin leer.
      //
      // Y son los que NO HE LEÍDO YO (mig 324), no los que nadie ha leído: antes
      // bastaba con que entrara un director para que los otros seis lo vieran
      // leído. Sin saber mis lecturas el contador no se inventa: `null`.
      // [badge] EL MISMO PREDICADO QUE EL CÍRCULO, y por eso sale de una función
      // compartida: `esCorreoEntranteDelClub`. Antes aquí decía
      // `ids.has(m.sender_id)` y el círculo contaba por destinatario, así que
      // medido en producción el círculo decía 4 y la suma de la lista 0, sin un
      // mensaje en común.
      const unread =
        misLeidos === null
          ? null
          : msgs.filter(
              (m) => esCorreoEntranteDelClub(m, enElBuzonSet, ids) && !misLeidos.has(m.id)
            ).length;
      out.push({ familia: f, lastMessage: sorted[0], unreadCount: unread });
    }
    return out.sort((a, b) => {
      const ta = a.lastMessage ? new Date(a.lastMessage.created_at).getTime() : 0;
      const tb = b.lastMessage ? new Date(b.lastMessage.created_at).getTime() : 0;
      return tb - ta;
    });
  }, [messages, familias, misLeidos, enElBuzonSet]);

  const familiasVisibles = useMemo(() => {
    if (!search) return conversaciones.map((c) => c.familia);
    // Buscando salen también las familias sin mensajes, para poder empezar una
    // conversación. Basta con que ACIERTE UN MIEMBRO: quien busca "Nike"
    // espera encontrar el hilo aunque se llame "Adidas y Nike".
    const q = search.toLowerCase();
    return familias.filter((f) => f.miembros.some((m) => m.full_name.toLowerCase().includes(q)));
  }, [conversaciones, familias, search]);

  const convMap = useMemo(() => {
    const m = new Map<string, Conversation>();
    conversaciones.forEach((c) => m.set(c.familia.key, c));
    return m;
  }, [conversaciones]);

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <button
          onClick={() => setTab('bandeja')}
          className={
            tab === 'bandeja'
              ? 'btn-gold flex items-center gap-2'
              : 'btn-ghost flex items-center gap-2'
          }
        >
          <MessageSquare size={16} />
          Mensajes
        </button>
        <button
          onClick={() => setTab('broadcast')}
          className={
            tab === 'broadcast'
              ? 'btn-gold flex items-center gap-2'
              : 'btn-ghost flex items-center gap-2'
          }
        >
          <Megaphone size={16} />
          Broadcast global
        </button>
      </div>

      {tab === 'bandeja' && (
        <div className="flex gap-0 h-[calc(100vh-160px)] rounded-xl overflow-hidden border border-bg-border">
          {/* ── Panel izquierdo ── */}
          <div className="w-80 flex-shrink-0 flex flex-col bg-bg-surface border-r border-bg-border">
            {/* Cabecera */}
            <div className="p-3 border-b border-bg-border flex items-center gap-2 flex-shrink-0">
              {showSearch ? (
                <>
                  <div className="relative flex-1">
                    <Search
                      size={13}
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary"
                    />
                    <input
                      ref={inputRef}
                      className="input pl-8 h-8 text-xs w-full"
                      placeholder="Buscar alumno o entrenador..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <button
                    onClick={() => {
                      setShowSearch(false);
                      setSearch('');
                    }}
                    className="text-text-secondary hover:text-white transition-colors flex-shrink-0"
                  >
                    <X size={16} />
                  </button>
                </>
              ) : (
                <>
                  <p className="font-oswald text-sm uppercase tracking-wider text-white flex-1">
                    Conversaciones
                  </p>
                  <button
                    onClick={() => setShowSearch(true)}
                    className="text-text-secondary hover:text-gold-primary transition-colors"
                    title="Buscar o iniciar conversación"
                  >
                    <Plus size={16} />
                  </button>
                </>
              )}
            </div>

            {/* Lo que no se pudo agrupar, dicho y no escondido. */}
            {avisoAgrupacion && (
              <p
                data-testid="aviso-agrupacion"
                className="px-3 py-2 text-xs text-amber-300 bg-amber-500/10 border-b border-amber-500/30"
              >
                {avisoAgrupacion}
              </p>
            )}

            {/* Lo que no se pudo saber de MIS lecturas (mig 324). Sin esto, los
                globos desaparecerían sin más y la bandeja se leería como «todo
                visto». */}
            {misLeidos === null && (
              <p
                data-testid="aviso-lecturas"
                className="px-3 py-2 text-xs text-amber-300 bg-amber-500/10 border-b border-amber-500/30"
              >
                No se ha podido comprobar qué has leído tú: los hilos están, pero no se marca
                cuáles tienes sin abrir. No los dé por leídos.
              </p>
            )}

            {/* Lista */}
            <div className="overflow-y-auto flex-1">
              {errorCarga ? (
                <div
                  className="p-4 text-error text-sm space-y-2"
                  data-testid="comunicaciones-error"
                >
                  <p>{errorCarga}</p>
                  <button
                    className="btn-ghost text-xs"
                    onClick={loadData}
                    data-testid="comunicaciones-reintentar"
                  >
                    Reintentar
                  </button>
                </div>
              ) : familiasVisibles.length === 0 ? (
                <p className="text-text-secondary text-sm p-4 text-center">
                  {search ? 'Sin resultados' : 'Sin conversaciones. Usa + para iniciar una.'}
                </p>
              ) : (
                familiasVisibles.map((f) => {
                  const conv = convMap.get(f.key);
                  const unread = conv?.unreadCount ?? null;
                  const lastMsg = conv?.lastMessage;
                  const ultimoDelBuzon = lastMsg ? idsDelHilo.includes(lastMsg.sender_id) : false;
                  // Quién escribió el último mensaje. Sin esto, para un
                  // director la respuesta del admin se lee igual que la
                  // pregunta de la familia. Y con dos hermanos hace falta lo
                  // otro: de cuál de los dos iba.
                  const firmaUltima = lastMsg
                    ? autorDelClub(lastMsg, ultimoDelBuzon, profile?.id) ||
                      miembroDelMensaje(lastMsg, ultimoDelBuzon, f.miembros)
                    : '';
                  const isSelected = selectedFamilia?.key === f.key;
                  const hasUnread = unread !== null && unread > 0;

                  return (
                    <button
                      key={f.key}
                      onClick={() => {
                        elegidoAManoRef.current = false;
                        setSelectedFamilia(f);
                        setShowSearch(false);
                        setSearch('');
                      }}
                      className={`w-full text-left px-4 py-3 border-b border-bg-border/60 hover:bg-bg-elevated transition-colors flex items-center gap-3 ${isSelected ? 'bg-bg-elevated border-l-2 border-l-gold-primary' : ''}`}
                    >
                      {/* Avatar */}
                      <div
                        className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold ${hasUnread ? 'bg-gold-primary text-bg-primary' : 'bg-bg-elevated text-text-secondary'}`}
                      >
                        {inicialesDeFamilia(f.miembros)}
                      </div>
                      {/* Texto */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <p
                            data-testid={`familia-${f.key}`}
                            className={`text-sm truncate ${hasUnread ? 'text-white font-semibold' : 'text-white'}`}
                          >
                            {f.nombre}
                          </p>
                          {lastMsg && (
                            <span
                              className={`text-xs flex-shrink-0 ${hasUnread ? 'text-gold-primary font-medium' : 'text-text-secondary'}`}
                            >
                              {msgTime(lastMsg.created_at)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-1 mt-0.5">
                          <p
                            className={`text-xs truncate ${hasUnread ? 'text-text-primary' : 'text-text-secondary'}`}
                          >
                            {lastMsg
                              ? firmaUltima
                                ? `${firmaUltima}: ${lastMsg.content}`
                                : lastMsg.content
                              : 'Sin mensajes'}
                          </p>
                          {unread !== null && unread > 0 && (
                            <span
                              data-testid="conversacion-sin-leer"
                              // El TEXTO se recorta a «9+» a los 10, así que el
                              // número exacto va aparte: la prueba de que el
                              // círculo de la barra lateral y la suma de esta
                              // lista dicen lo mismo no puede depender de un
                              // rótulo recortado.
                              data-sin-leer={unread}
                              className="w-5 h-5 bg-gold-primary rounded-full text-bg-primary text-xs flex items-center justify-center font-bold flex-shrink-0"
                            >
                              {unread > 9 ? '9+' : unread}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* ── Panel derecho ── */}
          <div className="flex-1 flex flex-col bg-bg-primary">
            {!selectedFamilia ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-text-secondary">
                  <MessageSquare size={48} className="mx-auto mb-3 opacity-20" />
                  <p className="text-sm">Selecciona una conversación</p>
                  <p className="text-xs mt-1 opacity-60">o usa + para iniciar una nueva</p>
                </div>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="px-5 py-3 border-b border-bg-border bg-bg-surface flex items-center gap-3 flex-shrink-0">
                  <div className="w-9 h-9 rounded-full bg-bg-elevated flex items-center justify-center text-xs font-bold text-text-secondary">
                    {inicialesDeFamilia(selectedFamilia.miembros)}
                  </div>
                  <div className="min-w-0">
                    <p className="font-oswald text-white truncate">{selectedFamilia.nombre}</p>
                    {selectedFamilia.miembros.length > 1 && (
                      <p className="text-xs text-text-secondary">
                        {selectedFamilia.miembros.length} en la misma familia
                      </p>
                    )}
                  </div>
                </div>

                {/* Mensajes */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {errorHilo ? (
                    <div className="text-center text-sm pt-8 space-y-2" data-testid="hilo-ilegible">
                      <p className="text-error">No se ha podido leer la conversación.</p>
                      <p className="text-text-secondary">
                        {errorHilo}. No la dé por vacía: puede tener historia y una respuesta
                        escrita encima quedaría fuera de contexto.
                      </p>
                      <button
                        className="btn-ghost text-xs"
                        onClick={() => selectedFamilia && loadThread(selectedFamilia)}
                        data-testid="hilo-reintentar"
                      >
                        Reintentar
                      </button>
                    </div>
                  ) : (
                    threadMessages.length === 0 && (
                      <p className="text-center text-text-secondary text-sm pt-8">
                        Sé el primero en enviar un mensaje
                      </p>
                    )
                  )}
                  {threadMessages.map((m) => {
                    // La pregunta es "¿es del buzón?", no "¿es mío?". Desde
                    // #483 el hilo trae los mensajes de TODO el buzón del club,
                    // y preguntando por el propio id un director no reconocía
                    // ninguno como saliente: los suyos, los del admin y los de
                    // la familia caían todos a la misma rama. Es el patrón que
                    // arregló #483 en la consulta, sobrevivido en el render.
                    const delBuzon = idsDelHilo.includes(m.sender_id);
                    const esMio = m.sender_id === profile?.id;
                    const autor = autorDelClub(m, delBuzon, profile?.id);
                    // De cuál de los hermanos va este mensaje. Vacío cuando la
                    // familia tiene un solo miembro: entonces ya lo dice la
                    // cabecera.
                    const miembro = miembroDelMensaje(m, delBuzon, selectedFamilia.miembros);
                    // Del lado del club la línea junta las dos cosas —quién lo
                    // escribió y a quién— porque en un hilo de dos hermanos
                    // "Tú" a secas no distingue nada.
                    const firma = delBuzon ? [autor, miembro].filter(Boolean).join(' → ') : miembro;
                    return (
                      <div
                        key={m.id}
                        data-testid={`msg-${m.id}`}
                        data-lado={delBuzon ? 'club' : 'familia'}
                        className={`flex ${delBuzon ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[72%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
                            esMio
                              ? 'bg-gold-primary text-bg-primary rounded-br-sm'
                              : delBuzon
                                ? 'bg-bg-elevated text-white border border-gold-primary/40 rounded-br-sm'
                                : 'bg-bg-surface text-white rounded-bl-sm'
                          }`}
                        >
                          {firma && (
                            <p
                              data-testid={delBuzon ? `msg-autor-${m.id}` : `msg-de-${m.id}`}
                              className={`text-xs font-semibold mb-1 ${esMio ? 'text-bg-primary/70' : 'text-gold-primary'}`}
                            >
                              {firma}
                            </p>
                          )}
                          <p className="leading-relaxed">{m.content}</p>
                          <p
                            className={`text-xs mt-1 text-right ${esMio ? 'text-bg-primary/60' : 'text-text-secondary'}`}
                          >
                            {format(new Date(m.created_at), 'HH:mm')}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>

                {/* Input */}
                <div className="p-4 border-t border-bg-border bg-bg-surface flex-shrink-0 space-y-2">
                  {/* Delante del cajón, no en un rincón: es lo que explica que
                      el mensaje no esté en el hilo. */}
                  {errorEnvio && (
                    <p className="text-sm text-error" data-testid="envio-fallido" role="alert">
                      {errorEnvio}
                    </p>
                  )}
                  {/* Y si el botón está apagado por algo que no se ve, lo dice
                      sin esperar a que nadie lo pulse. */}
                  {!errorEnvio && !destinatarioId && (
                    <p className="text-sm text-text-secondary" data-testid="envio-bloqueado">
                      {MOTIVO_SIN_DESTINATARIO}
                    </p>
                  )}
                  <div className="flex gap-2 items-center">
                    {/* El hilo es de la casa, pero el mensaje va a una persona:
                      `messages` sigue siendo persona a persona (la 277 no toca
                      ni una fila) y cada hijo solo puede leer lo suyo. Con dos
                      hermanos, decidir a cuál se contesta no puede quedar
                      implícito. */}
                    {selectedFamilia.miembros.length > 1 && (
                      <label className="flex items-center gap-1.5 text-xs text-text-secondary flex-shrink-0">
                        Para
                        <select
                          aria-label="Destinatario de la respuesta"
                          className="input h-10 text-sm py-0"
                          value={destinatarioId ?? ''}
                          onChange={(e) => {
                            elegidoAManoRef.current = true;
                            setDestinatarioId(e.target.value);
                          }}
                        >
                          {selectedFamilia.miembros.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.full_name}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    <input
                      className="input flex-1 h-10 text-sm"
                      placeholder="Escribe un mensaje..."
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendReply()}
                    />
                    <button
                      onClick={sendReply}
                      disabled={motivoEnvioDeshabilitado !== null}
                      title={motivoEnvioDeshabilitado ?? 'Enviar'}
                      aria-label={motivoEnvioDeshabilitado ?? 'Enviar'}
                      className="btn-gold px-4 h-10 flex items-center gap-2"
                    >
                      <Send size={15} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'broadcast' && (
        <div className="max-w-2xl card space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <Users size={20} className="text-gold-primary" />
            <p className="text-text-secondary text-sm">
              Este mensaje queda en la bandeja de avisos de{' '}
              <strong className="text-white">todas</strong> las cuentas activas —alumnos, tutores,
              entrenadores y dirección— y en el chat. El aviso al móvil solo le suena a quien tiene
              la app instalada y los avisos encendidos; al enviar se dice a cuántos.
            </p>
          </div>
          <div>
            <label className="label">Título del mensaje *</label>
            <input
              className="input"
              placeholder="Ej: Festivos de Navidad"
              value={broadcastTitle}
              onChange={(e) => setBroadcastTitle(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Contenido *</label>
            <textarea
              className="input resize-none"
              rows={4}
              placeholder="Escribe el mensaje para todo el club..."
              value={broadcastText}
              onChange={(e) => setBroadcastText(e.target.value)}
            />
          </div>
          <div className="flex justify-end">
            <button
              onClick={sendBroadcast}
              disabled={sending || !broadcastTitle.trim() || !broadcastText.trim()}
              className="btn-gold flex items-center gap-2"
            >
              <Megaphone size={16} /> {sending ? 'Enviando...' : 'Enviar a todos'}
            </button>
          </div>

          {/* ── [345] EL PARTE DE CADA ANUNCIO ───────────────────────────
              Hasta ahora el parte se pintaba en un `alert()` y se tiraba a la
              basura: no había dónde volver a verlo. Esto es ese sitio. Los
              LEÍDOS se calculan de los avisos, así que el número de abajo sube
              a medida que la gente los abre. */}
          <div className="border-t border-bg-border pt-4 mt-2" data-testid="anuncios-historico">
            <h3 className="text-white font-semibold mb-1">Anuncios enviados</h3>
            <p className="text-text-secondary text-xs mb-3">
              Cuántos lo recibieron en su bandeja, cuántos lo han leído ya y en cuántos móviles
              sonó. Los leídos se cuentan en vivo.
            </p>

            {errorAnuncios && (
              <p className="text-red-400 text-sm" data-testid="anuncios-error">
                No se ha podido leer el histórico de anuncios: {errorAnuncios}
              </p>
            )}

            {!errorAnuncios && anuncios === null && (
              <p className="text-text-secondary text-sm">Cargando…</p>
            )}

            {!errorAnuncios && anuncios !== null && anuncios.length === 0 && (
              <p className="text-text-secondary text-sm" data-testid="anuncios-vacio">
                Todavía no se ha enviado ningún anuncio.
              </p>
            )}

            {!errorAnuncios && anuncios !== null && anuncios.length > 0 && (
              <ul className="space-y-3">
                {anuncios.map((a) => (
                  <li
                    key={a.anuncio_id ?? a.tanda}
                    className="bg-bg-base rounded-lg p-3"
                    data-testid="anuncio-fila"
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="text-white text-sm font-medium">{a.titulo ?? 'Sin título'}</p>
                      <p className="text-text-secondary text-xs whitespace-nowrap">
                        {format(new Date(a.tanda), "d MMM yyyy, HH:mm")}
                      </p>
                    </div>
                    <p className="text-text-secondary text-xs mt-1" data-testid="anuncio-lecturas">
                      {a.escritos} en su bandeja · <strong className="text-white">{a.leidos} leído{a.leidos === 1 ? '' : 's'}</strong>
                      {a.descartados > 0 && ` · ${a.descartados} descartado${a.descartados === 1 ? '' : 's'}`}
                    </p>

                    {/* El parte solo existe para los anuncios que salieron por
                        la EF. Para los de antes NO se inventa: se dice por qué
                        no está, que es más útil que un cero. */}
                    {a.parte_en !== null ? (
                      <p className="text-text-secondary text-xs mt-1" data-testid="anuncio-parte">
                        Sonó en <strong className="text-white">{a.entregados}</strong> de {a.moviles}{' '}
                        móviles
                        {/* [346] El número que baja, con su motivo al lado. Es
                            NULL en los partes anteriores a la 346 —entonces no
                            se colapsaba nada— y ahí no se dice: un 0 afirmaría
                            una medición que no hubo. */}
                        {(a.colapsados ?? 0) > 0 && (
                          <span data-testid="anuncio-colapsados">
                            {' '}
                            ({a.destinatarios} personas · {a.colapsados} comparte
                            {a.colapsados === 1 ? '' : 'n'} teléfono)
                          </span>
                        )}
                        {(a.sin_movil ?? 0) > 0 && ` · ${a.sin_movil} sin móvil`}
                        {(a.fallos ?? 0) > 0 && (
                          <span className="text-red-400"> · {a.fallos} fallo(s) de entrega</span>
                        )}
                        {a.motivos && Object.keys(a.motivos).length > 0 && (
                          <span className="block text-[11px] mt-0.5">
                            {desgloseDeMotivos(a.motivos, ' · ')}
                          </span>
                        )}
                      </p>
                    ) : a.sellado ? (
                      <p className="text-amber-400 text-xs mt-1" data-testid="anuncio-sin-parte">
                        Se intentó y no se completó: no hay parte de entrega. Es lo que esta
                        pantalla existe para que no pase inadvertido.
                      </p>
                    ) : (
                      <p className="text-text-secondary text-xs mt-1" data-testid="anuncio-viejo">
                        Anterior al registro de partes: sus avisos no llevan sello, así que no se
                        sabe en cuántos móviles sonó. Los leídos sí se cuentan.
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
